import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonFooter,
  IonSpinner,
  IonText,
  IonTextarea,
  IonButton,
  IonIcon,
} from '@ionic/react';
import { arrowDownOutline, refreshOutline, sendOutline, stop } from 'ionicons/icons';
import { useAiConversation } from '../../application/use-cases/ai/useAiConversation';
import AiMessageBubble from '../components/ai/AiMessageBubble';
import './AiAssistant.css';

// A menos de esto del final se considera "abajo de todo": el chat sigue la
// respuesta mientras se escribe; si el usuario subió a leer, no lo mueve.
const BOTTOM_THRESHOLD_PX = 64;

const AiConversationThread: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { conversation, messages, loading, sending, error, aiDisabled, liveKey, sendMessage, stop: stopAnswer, retry } =
    useAiConversation(id);
  const [question, setQuestion] = useState('');

  const contentRef = useRef<HTMLIonContentElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const scrollElRef = useRef<HTMLElement | null>(null);
  const stickToBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollElRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    contentRef.current
      ?.getScrollElement?.()
      .then((el) => {
        scrollElRef.current = el;
        scrollToBottom();
      })
      .catch(() => undefined);
  }, [scrollToBottom]);

  // Cada vez que el hilo cambia de alto (llega texto, se dibuja un gráfico,
  // se abre el razonamiento) y el usuario está abajo, lo sigue. También es lo
  // que lleva al último mensaje al entrar a la conversación.
  useEffect(() => {
    const thread = threadRef.current;
    if (!thread || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (stickToBottom.current) scrollToBottom();
    });
    observer.observe(thread);
    return () => observer.disconnect();
  }, [loading, scrollToBottom]);

  const handleScroll = () => {
    const el = scrollElRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_THRESHOLD_PX;
    stickToBottom.current = atBottom;
    setShowJump(!atBottom);
  };

  const jumpToBottom = () => {
    stickToBottom.current = true;
    setShowJump(false);
    scrollToBottom(true);
  };

  const handleSend = () => {
    if (!question.trim() || sending) return;
    sendMessage(question.trim());
    setQuestion('');
    jumpToBottom();
  };

  // Enter manda el mensaje; Shift+Enter agrega un salto de línea (estándar en chats).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const hasFailed = messages.some((m) => m.status === 'failed');

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/ai" />
          </IonButtons>
          <IonTitle>{conversation?.title ?? 'Conversación'}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={contentRef} fullscreen className="ion-padding" scrollEvents onIonScroll={handleScroll}>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && aiDisabled && (
          <IonText color="medium">
            <p>El asistente de IA todavía no está activado para tu cuenta.</p>
          </IonText>
        )}

        {!loading && (
          <div className="ai-thread" ref={threadRef}>
            {messages.map((m) => (
              <AiMessageBubble key={m.key} message={m} animate={m.key === liveKey} />
            ))}

            {error && (
              <div className="ai-error">
                <IonText color="danger">
                  <p>{error}</p>
                </IonText>
                {hasFailed && (
                  <IonButton size="small" fill="outline" onClick={retry} disabled={sending}>
                    <IonIcon icon={refreshOutline} slot="start" />
                    Reintentar
                  </IonButton>
                )}
              </div>
            )}
          </div>
        )}

        {showJump && (
          <div slot="fixed" className="ai-jump">
            <button type="button" onClick={jumpToBottom} aria-label="Ir al último mensaje">
              <IonIcon icon={arrowDownOutline} />
            </button>
          </div>
        )}
      </IonContent>
      <IonFooter className="ion-no-border">
        <div className="ai-input-row ion-padding-horizontal">
          <IonTextarea
            value={question}
            placeholder="Escribí tu pregunta..."
            autoGrow
            rows={1}
            onIonInput={(e) => setQuestion(e.detail.value ?? '')}
            onKeyDown={handleKeyDown}
          />
          {sending ? (
            <IonButton onClick={stopAnswer} aria-label="Detener respuesta" color="medium">
              <IonIcon icon={stop} slot="icon-only" />
            </IonButton>
          ) : (
            <IonButton disabled={!question.trim()} onClick={handleSend} aria-label="Enviar">
              <IonIcon icon={sendOutline} slot="icon-only" />
            </IonButton>
          )}
        </div>
      </IonFooter>
    </IonPage>
  );
};

export default AiConversationThread;
